import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { DataTableColumn } from '~/components/data-table/data-table.component';
import { ValidatorService, ReviewRecord, RecordDetailResponse } from '~/services/validator.service';

interface DocumentTableRow {
    document_id: string;
    status: string;
    total_sections: number;
    critical: number;
    not_critical: number;
    file_name?: string;
    action?: string;
}

interface ClassificationTableRow {
    record_id: string;
    document_id: string;
    section_index: number;
    section_title: string;
    classification: string;
    statementId: string;
    classification_reason: string;
    validator_reason: string;
    validation_status: string;
    view_image: string;
    action?: string;
    sectionimage_path: string;
    revision_statement: string;
}

@Component({
    selector: 'app-record-detail',
    templateUrl: './record-detail.component.html',
    styleUrls: ['./record-detail.component.scss'],
    standalone: false
})
export class RecordDetailComponent implements OnInit {
    recordId: string | null = null;
    record: ReviewRecord | null = null;
    documentRows: DocumentTableRow[] = [];
    classificationRowsByDocumentId: Record<string, ClassificationTableRow[]> = {};
    documentLoadMessage: string | null = null;
    isDocumentLoading: boolean = false;
    isImageModalOpen: boolean = false;
    isImageLoading: boolean = false;
    imageLoadError: string | null = null;
    imageUrls: string[] = [];
    imageModalTitle: string = 'Section Images';
    currentImageIndex: number = 0;
    approveModalTitle: string = 'Approve Classification';
    approveOriginalClassification: string = '';
    approveSelectedClassification: string = '';
    approveValidatorReason: string = '';
    approveTargetRow: ClassificationTableRow | null = null;

    public readonly documentDetailsColumns: DataTableColumn<DocumentTableRow>[] = [
        { key: 'document_id', header: 'Document ID' },
        { key: 'file_name', header: 'Document Name' },
        { key: 'status', header: 'Status' },
        { key: 'total_sections', header: 'Total Sections', align: 'right' },
        { key: 'critical', header: 'Critical', align: 'right' },
        { key: 'not_critical', header: 'Not Critical', align: 'right' },
        { key: 'action', header: 'Approved', align: 'center' }
    ];

    public readonly classificationColumns: DataTableColumn<ClassificationTableRow>[] = [
        { key: 'section_title', header: 'Section Title' },
        { key: 'revision_statement', header: 'Revision Statement', align: 'right' },
        { key: 'classification', header: 'Classification' },
        { key: 'classification_reason', header: 'Classification Reason' },
        { key: 'validator_reason', header: 'Validator Reason' },
        { key: 'validation_status', header: 'Validation Status' },
        { key: 'view_image', header: 'View Image', align: 'center' },
    ];

    constructor(
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly validatorService: ValidatorService
    ) { }

    /**
     * Navigates back to the home page.
     */
    public goToHome(): void {
        this.router.navigate(['/']);
    }

    /**
     * Lifecycle hook that is called after the component has been initialized. It triggers the initial fetch of record details from the backend service to populate the data table displayed on the record detail page. This method ensures that the component starts with the most up-to-date data when it is rendered for the user.
     */
    ngOnInit(): void {
        this.recordId = this.route.snapshot.paramMap.get('id');

        // Get record summary from router state (passed from home component)
        const recordData = (history.state?.record as ReviewRecord | undefined);
        if (recordData) {
            this.record = recordData;
        }

        // Get full detail response from router state if available (from home component pre-fetch)
        const detailResponse = (history.state?.recordResponse as RecordDetailResponse | undefined);

        if (detailResponse) {
            // Use the detail response passed from home component
            this.documentRows = this.mapDocumentRows(detailResponse);
            this.classificationRowsByDocumentId = this.mapClassificationRowsByDocumentId(detailResponse);
            this.documentLoadMessage = this.documentRows.length === 0 ? 'No documents found for this record.' : null;
            this.isDocumentLoading = false;
        } else if (this.recordId) {
            // Fallback: fetch detail if not passed via state
            this.fetchRecordDetail();
        }
    }

    /**
     * Fetches the detailed information for a specific record from the backend service.
     * It updates the component state with the fetched data, including document rows and classification rows.
     * Handles loading state and error messages appropriately.
     */
    public fetchRecordDetail(): void {
        if (!this.recordId) return;

        this.isDocumentLoading = true;
        this.documentLoadMessage = null;

        this.validatorService.getRecordDetail(this.recordId).subscribe({
            next: (response: RecordDetailResponse) => {
                this.documentRows = this.mapDocumentRows(response);
                this.classificationRowsByDocumentId = this.mapClassificationRowsByDocumentId(response);
                this.documentLoadMessage = this.documentRows.length === 0 ? 'No documents found for this record.' : null;
                this.isDocumentLoading = false;
            },
            error: (error: HttpErrorResponse) => {
                if (error.status === 404) {
                    this.documentRows = [];
                    this.classificationRowsByDocumentId = {};
                    this.documentLoadMessage = 'No documents found for this record.';
                    this.isDocumentLoading = false;
                    return;
                }

                this.documentRows = [];
                this.classificationRowsByDocumentId = {};
                this.documentLoadMessage = 'Unable to load document details right now.';
                this.isDocumentLoading = false;
            }
        });
    }

    /**
     * Maps the document rows from the record detail response to the format used by the data table.
     * @param response - The record detail response from the backend service.
     * @returns An array of document table rows.
     */
    private mapDocumentRows(response?: RecordDetailResponse): DocumentTableRow[] {
        if (!response?.documents?.length) {
            return [];
        }

        return response.documents.map((document) => ({
            document_id: document.document_id,
            status: document.status,
            total_sections: document.classification_summary?.total_sections ?? 0,
            critical: document.classification_summary?.critical ?? 0,
            not_critical: document.classification_summary?.not_critical ?? 0,
            file_name: document.file_name
        }));
    }

    /**
     * 
     * @param response - The record detail response from the backend service.
     * @returns - A mapping of document IDs to their corresponding classification table rows, which include details about each section's classification and related information.
     */
    private mapClassificationRowsByDocumentId(response?: RecordDetailResponse): Record<string, ClassificationTableRow[]> {
        if (!response?.documents?.length) {
            return {};
        }

        return response.documents.reduce((acc, document) => {
            acc[document.document_id] = (document.classifications ?? []).map((item) => ({
                record_id: response.record_id,
                document_id: document.document_id,
                section_index: item.section_index,
                section_title: item.section_title,
                classification: item.classification,
                classification_reason: item.classification_reason,
                validator_reason: item.validator_reason ?? '--',
                validation_status: item.validation_status,
                view_image: 'View Image',
                action: 'Approve',
                statementId: item.statementId,
                revision_statement: item.revision_statement,
                sectionimage_path: item.sectionimage_path
            }));
            return acc;
        }, {} as Record<string, ClassificationTableRow[]>);
    }

    /**
     * @param row - The classification table row that the user wants to approve. This method initializes the approval modal with the current classification details of the selected section, allowing the user to review and modify the classification if necessary before submitting their approval. It sets up the modal state, including the original classification, selected classification, and any validator reason, and then opens the modal for user interaction.
     */
    public onApproveClassification(row: Record<string, unknown>): void {
        const classificationRow = row as unknown as ClassificationTableRow;
        const currentClassification = this.normalizeClassificationValue(classificationRow.classification);

        this.approveTargetRow = classificationRow;
        this.approveOriginalClassification = currentClassification;
        this.approveSelectedClassification = currentClassification;
        this.approveValidatorReason = '';
        this.approveModalTitle = `${classificationRow.document_id} - ${classificationRow.section_title}`;
    }

    public get isClassificationChanged(): boolean {
        return this.approveSelectedClassification !== ''
            && this.approveSelectedClassification !== this.approveOriginalClassification;
    }

    public get showValidatorReasonField(): boolean {
        return this.isClassificationChanged;
    }

    public get canSubmitApprove(): boolean {
        if (!this.approveTargetRow || this.approveSelectedClassification === '') {
            return false;
        }

        if (!this.isClassificationChanged) {
            return true;
        }

        return this.approveValidatorReason.trim().length > 0;
    }

    public get classificationOptions(): string[] {
        if (this.approveOriginalClassification === 'critical') {
            return ['critical', 'not critical'];
        }

        if (this.approveOriginalClassification === 'not critical') {
            return ['not critical', 'critical'];
        }

        return ['critical', 'not critical'];
    }


    /**
     * Normalizes the classification value to ensure consistency in how classifications are represented throughout the application.
     * This method trims any whitespace, converts the value to lowercase, and standardizes certain variations of "critical" and "not critical".
     * @param value - The classification value that needs to be normalized.
     * @returns The normalized classification value.
     */
    private normalizeClassificationValue(value: string): string {
        const normalized = (value || '').trim().toLowerCase();

        if (normalized.includes('not critical') || normalized === 'non critical' || normalized === 'non-critical') {
            return 'not critical';
        }

        if (normalized === 'critical') {
            return 'critical';
        }

        return value;
    }

    public onViewImage(row: Record<string, unknown>): void {
        const recordId = row['record_id'] as string | undefined;
        const documentId = row['document_id'] as string | undefined;
        const sectionIndex = row['section_index'] as number | undefined;
        const sectionTitle = (row['section_title'] as string | undefined) ?? 'Section Images';

        if (!recordId || !documentId || sectionIndex === undefined || sectionIndex === null) {
            this.imageLoadError = 'Missing section details required to load images.';
            this.isImageModalOpen = true;
            return;
        }

        this.isImageModalOpen = true;
        this.isImageLoading = true;
        this.imageLoadError = null;
        this.imageUrls = [];
        this.currentImageIndex = 0;
        this.imageModalTitle = `${documentId} - ${sectionTitle}`;

        this.validatorService.getSectionImages(recordId, documentId, sectionIndex).subscribe({
            next: (response) => {
                this.imageUrls = (response.images ?? [])
                    .map((image) => image.url)
                    .filter((url) => !!url);

                if (this.imageUrls.length === 0) {
                    this.imageLoadError = 'No images available for this section.';
                }

                this.currentImageIndex = 0;

                this.isImageLoading = false;
            },
            error: (error: HttpErrorResponse) => {
                this.imageLoadError = error.status === 404
                    ? 'No images found for this section.'
                    : 'Unable to load section images right now.';
                this.isImageLoading = false;
            }
        });
    }

    /**
     * Closes the image modal and resets the modal state to its initial values. This method is typically called when the user finishes viewing the images or cancels the image modal, ensuring that the modal is ready for the next interaction without any residual data from the previous section's images.
     */
    public closeImageModal(): void {
        this.isImageModalOpen = false;
        this.isImageLoading = false;
        this.imageLoadError = null;
        this.imageUrls = [];
        this.currentImageIndex = 0;
        this.imageModalTitle = 'Section Images';
    }

    public get currentImageUrl(): string {
        if (!this.imageUrls.length) {
            return '';
        }

        return this.imageUrls[this.currentImageIndex] ?? '';
    }

    /**
     * @returns - Navigates to the previous image in the list of image URLs. If the current image is the first one, it wraps around to the last image. This method is typically called when the user clicks a "Previous" button in the image modal, allowing them to cycle through all available images for a section.
     */
    public showPreviousImage(): void {
        if (this.imageUrls.length <= 1) {
            return;
        }

        this.currentImageIndex =
            this.currentImageIndex === 0
                ? this.imageUrls.length - 1
                : this.currentImageIndex - 1;
    }

    /**
     * @returns - Advances to the next image in the list of image URLs. If the current image is the last one, it wraps around to the first image. This method is typically called when the user clicks a "Next" button in the image modal, allowing them to cycle through all available images for a section.
     */
    public showNextImage(): void {
        if (this.imageUrls.length <= 1) {
            return;
        }

        this.currentImageIndex =
            this.currentImageIndex === this.imageUrls.length - 1
                ? 0
                : this.currentImageIndex + 1;
    }

    /**
     * Arrow function that returns the button label per row based on validation_status.
     * Used as [childSecondarySingleActionLabelFn] to dynamically set button text per row.
     */
    public getButtonStatusFn = (row: Record<string, unknown>): string => {
        const status = row['validation_status'] as string;
        if (status === 'approved') {
            return 'Approved';
        } else if (status === 'pending') {
            return 'Approve';
        }
        return 'Approve';
    };

    /**
     * @returns - A string representing the button status based on the validation status of the classification rows. If any row has a validation status of "approved", it returns "Approved". If any row has a validation status of "pending", it returns "Approve". If there are no rows or none of the rows have these statuses, it returns an empty string. This method is used to determine the appropriate label for action buttons in the UI based on the current state of classifications.
     */
    public getButtonStatus(): string {
        let returnStatus = "";
        for (const documentId in this.classificationRowsByDocumentId) {
            if (this.classificationRowsByDocumentId.hasOwnProperty(documentId)) {
                const rows = this.classificationRowsByDocumentId[documentId];
                for (const row of rows) {
                    if (row.validation_status === "approved") {
                        returnStatus = "Approved";
                    } else if (row.validation_status === "pending") {
                        returnStatus = "Approve";
                    }
                }
            }
        }
        return returnStatus;
    }

}
